import { Fragment, ReactNode, useCallback, useMemo } from 'react';
import { isEqual } from 'lodash';
import { useLayoutComponentEditorStore } from './useLayoutComponentEditorStore';
import { ILayoutComponentEditorProps } from './LayoutComponentEditor.types';
import {
  ILayoutComponentPropertyEditorProps,
  useAssistantLoadingStore,
  useFlowBuilder2Store,
} from '@diana/core';
import { useFlowBuilder2PageStore } from '../../../../Store/useFlowBuilder2PageStore';

interface IRenderHookProps<A, T> {
  hook: (args: A) => T;
  args: A;
  children: (result: T) => ReactNode;
}

interface IDynamicHookProps<A, T> {
  hook?: (args: A) => T;
  args: A;
  children: (result: T | null) => ReactNode;
}

export function RenderHook<A, T>({ hook: useHook, args, children }: IRenderHookProps<A, T>) {
  const result = useHook(args);
  return <Fragment>{children(result)}</Fragment>;
}

export function RenderDynamicHook<A, T>({ hook, args, children }: IDynamicHookProps<A, T>) {
  if (!hook) return <Fragment>{children(null)}</Fragment>;
  return (
    <RenderHook hook={hook} args={args}>
      {children}
    </RenderHook>
  );
}

export const useSubEditorProps = (props: ILayoutComponentEditorProps) => {
  const { value } = props;

  const subEditor = useLayoutComponentEditorStore((s) => s.getSubEditor(), isEqual);
  const openSubEditor = useLayoutComponentEditorStore((s) => s.openSubEditor);
  const onCloseSubEditor = useLayoutComponentEditorStore((s) => s.closeSubEditor);

  const fieldName = subEditor?.editor?.name;

  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);

  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);

  const fieldState = useFlowBuilder2Store((s) => {
    if (!fieldName) return null;
    return s.getFieldState(value.id, 'layoutComponent', fieldName);
  }, isEqual);
  const setFieldState = useFlowBuilder2Store((s) => s.setFieldState);

  const options = useMemo(() => {
    return subEditor?.editor?.options ?? {};
  }, [subEditor?.editor?.options]);

  const disabled = isChatLoading || hasAnyPendingStatus;

  const onTouched = useCallback<ILayoutComponentPropertyEditorProps['onTouched']>(() => {
    if (fieldName) {
      setFieldState(value.id, 'layoutComponent', fieldName, (draft) => {
        draft.touched = true;
        return draft;
      });
    }
  }, [fieldName, setFieldState, value.id]);

  const onOpenSubEditor = useCallback<ILayoutComponentPropertyEditorProps['onOpenSubEditor']>(
    (useSubEditor) => {
      if (subEditor) {
        openSubEditor({ useSubEditor, editor: subEditor.editor });
      }
    },
    [openSubEditor, subEditor],
  );

  const subEditProps: ILayoutComponentPropertyEditorProps = {
    ...props,
    options,
    disabled,
    dirty: fieldState?.dirty ?? false,
    touched: fieldState?.touched ?? false,
    onTouched,
    onOpenSubEditor,
    onCloseSubEditor,
  };

  return subEditProps;
};
